import { memo } from 'react';
import { LABEL_STYLE } from '../shared/constants';
import { InnovationTooltip } from '../shared/InnovationTooltip';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList,
} from 'recharts';
import { ChartWrapper } from '../shared/ChartWrapper';
import { NoChartData } from '../shared/NoChartData';
import { useInnovationData } from './useInnovationData';

export const InnovationReadinessLevelChart = memo(function InnovationReadinessLevelChart() {
  const { readinessDistribution, innovationResults } = useInnovationData();

  const hasData = readinessDistribution && Object.keys(readinessDistribution).length > 0;
  if (!hasData) return (
    <ChartWrapper title="Innovation Readiness Level Distribution">
      <NoChartData message="No readiness-level scores assigned" detail='This chart visualizes Innovation Readiness Level ratings (scale 1-9). None of the innovation results in the current selection have a readiness-level score.' />
    </ChartWrapper>
  );

  const levels = [1, 2, 3, 4, 5, 6, 7, 8, 9];
  const data = levels.map(l => ({ level: `Level ${l}`, count: readinessDistribution[l] || 0 }));
  const totalN = Object.values(readinessDistribution).reduce((s, v) => s + v, 0);
  const exportData = levels.map(l => ({ Level: l, Count: readinessDistribution[l] || 0 }));

  return (
    <ChartWrapper title="Innovation Readiness Level Distribution" info="Distribution of Innovation Readiness Levels (1–9) across innovation development results. Higher levels indicate closer to market readiness." chartData={exportData} exportFilename="innovation-readiness-level" sourceResults={innovationResults}>
      <p className="text-sm text-gray-500 mb-3">
        Maturity profile across <strong>{totalN} innovations</strong> on the 1-9 readiness scale, from early-stage research to proven at scale.
      </p>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={data} margin={{ top: 20 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="level" tick={{ fontSize: 11 }} interval={0} />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip content={<InnovationTooltip unit="innovations" labelKey="level" />} />
          <Bar dataKey="count" fill="#14B8A6" radius={[4, 4, 0, 0]}>
            <LabelList dataKey="count" position="top" style={LABEL_STYLE} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <p className="text-xs text-gray-400 text-center mt-1">Based on {totalN} innovation results</p>
    </ChartWrapper>
  );
});
